import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { api } from "../../../api/apiService";
import { Link } from "react-router-dom";
import DeleteModal from "../../../components/Modal/DeleteModal";
import DepartmentModal from "../../../components/Modal/DepartmentModal";

const Department = () => {
  const [departments, setDepartments] = useState([]);
  const [showModal, setShowModal] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentDepartment, setCurrentDepartment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await api.get("/departments");
      setDepartments(response.data.data);
    } catch (error) {
      console.error("Error fetching departments: ", error);
      toast.error("Failed to fetch departments");
    }
  };

  const handleAddDepartment = () => {
    setCurrentDepartment({
      department_name: "",
      description: "",
    });
    setIsEditing(false);
    setShowModal("data");
  };

  const handleEditDepartment = (department) => {
    setCurrentDepartment(department);
    setIsEditing(true);
    setShowModal("data");
  };

  const handleDeleteDepartment = async () => {
    if (!currentDepartment) return;
    try {
      setIsLoading(false);
      await api.delete(`/departments/${currentDepartment?.department_id}`);
      setDepartments((prev) =>
        prev.filter(
          (dept) => dept.department_id !== currentDepartment?.department_id
        )
      );
      toast.success("Department deleted successfully");
    } catch (error) {
      console.error("Error deleting department: ", error);
      toast.error("Error deleting department");
    } finally {
      setIsLoading(false);
      setShowModal(null);
    }
  };

  const saveDepartment = async (data) => {
    setIsLoading(true);
    try {
      if (isEditing && currentDepartment) {
        const departmentId = currentDepartment.department_id;

        if (!departmentId) {
          throw new Error("Department ID is undefined");
        }

        await api.put(`/departments/${departmentId}`, data);
        toast.success("Department updated successfully");
      } else {
        await api.post("/departments", data);
        toast.success("Department added successfully");
      }
      fetchDepartments();
      setShowModal(null);
    } catch (error) {
      console.error("Error saving department: ", error);
      toast.error(error.data.error || "Error saving department");
    } finally {
      setIsLoading(false);
      setCurrentDepartment(null);
    }
  };

  return (
    <main id="main" className="main">
      <div className="pagetitle">
        <h1>Departments</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/home">Home</Link>
            </li>
            <li className="breadcrumb-item active">Departments</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Department Management</h5>
                <div>
                  <button
                    className="btn btn-primary mb-3"
                    onClick={handleAddDepartment}
                  >
                    Add Department
                  </button>
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departments.map((department, index) => (
                        <tr key={department.department_id || index}>
                          <td>{department.department_name}</td>
                          <td>{department.description}</td>
                          <td>
                            <button
                              className="btn btn-warning btn-sm me-2"
                              onClick={() => handleEditDepartment(department)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => {
                                setCurrentDepartment(department);
                                setShowModal("delete");
                              }}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {showModal === "data" && (
                    <DepartmentModal
                      show={true}
                      onClose={() => setShowModal(null)}
                      onSubmit={saveDepartment}
                      isLoading={isLoading}
                      isEditing={isEditing}
                      defaultValues={{
                        department_name:
                          currentDepartment?.department_name || "",
                        description: currentDepartment?.description || "",
                      }}
                    />
                  )}
                  {showModal === "delete" && (
                    <DeleteModal
                      show={true}
                      onClose={() => setShowModal(null)}
                      onDelete={handleDeleteDepartment}
                      isLoading={isLoading}
                      body={
                        <>
                          Are you sure you want to delete the department{" "}
                          <strong className="text-capitalize">
                            {currentDepartment?.department_name}
                          </strong>
                          ?
                        </>
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Department;
